import {
  ComplianceHistoryFilters,
  FacilityFilters,
  NetworkFilters,
  ServiceOrderFilters,
  SystemFilters,
} from "@features/ax/assetRegistry/facilities/types";
import {
  ActionType,
  EntityType,
  ModuleType,
} from "@shared/api/queries/queryKeyFactory";

export const facilitiesKeyFactory = {
  getConditionHistory: (
    facilityId: number,
    entityType?: number,
    page?: number
  ): [ModuleType, EntityType, ActionType, number, number, number] => [
    "ax",
    "facilities",
    "getConditionHistory",
    facilityId,
    entityType,
    page,
  ],

  getSystemConditionAndRiskById: (
    assessmentName: string,
    facilityId: number,
    remoteUtilityId: string
  ): [ModuleType, EntityType, ActionType, string, number, string] => [
    "ax",
    "facilities",
    "getSystemConditionAndRiskById",
    assessmentName,
    facilityId,
    remoteUtilityId,
  ],

  compliance: (
    facilityId: string,
    remoteUtilityId: string
  ): [ModuleType, EntityType, ActionType, string, string] => [
    "ax",
    "facilities",
    "compliance",
    facilityId,
    remoteUtilityId,
  ],
  complianceHistory: (
    facilityId: string,
    remoteUtilityId: string,
    filters: ComplianceHistoryFilters
  ): [
    ModuleType,
    EntityType,
    ActionType,
    string,
    string,
    ComplianceHistoryFilters
  ] => [
    "ax",
    "facilities",
    "complianceHistory",
    facilityId,
    remoteUtilityId,
    filters,
  ],
  alerts: (
    facilityId: string,
    remoteUtilityId: string
  ): [ModuleType, EntityType, ActionType, string, string] => [
    "ax",
    "facilities",
    "alerts",
    facilityId,
    remoteUtilityId,
  ],
  dashboard: (
    remoteUtilityId: string
  ): [ModuleType, EntityType, ActionType, string] => [
    "ax",
    "facilities",
    "summary",
    remoteUtilityId,
  ],
  // Facilities list with filters
  list: (
    filters?: Record<string, any>
  ): [ModuleType, EntityType, ActionType, Record<string, any>] => [
    "ax",
    "facilities",
    "list",
    filters,
  ],
  facilityFilter: (
    filters?: any
  ): [ModuleType, EntityType, ActionType, any?] => [
    "ux",
    "uxTemplate",
    "filtered",
    filters,
  ],

  // Individual facility details
  details: (
    facilityId: string,
    remoteUtilityId: string
  ): [ModuleType, EntityType, ActionType, string, string] => [
    "ax",
    "facilities",
    "details",
    facilityId,
    remoteUtilityId,
  ],

  // Facility performance data
  performance: (
    facilityId: number,
    remoteUtilityId: string
  ): [ModuleType, EntityType, ActionType, number, string] => [
    "ax",
    "facilities",
    "performance",
    facilityId,
    remoteUtilityId,
  ],
  systems: {
    list: (
      facilityId: string,
      filters: FacilityFilters = {}
    ): [ModuleType, EntityType, ActionType, string, SystemFilters] => [
      "ax",
      "facilities",
      "performance",
      facilityId,
      filters,
    ],
  },

  // Choice/dropdown data
  choices: {
    statuses: (): [ModuleType, EntityType, ActionType, "statuses"] => [
      "ax",
      "facilities",
      "choices",
      "statuses",
    ],

    operationalHours: (): [
      ModuleType,
      EntityType,
      ActionType,
      "operational_hours"
    ] => ["ax", "facilities", "choices", "operational_hours"],

    depreciationSchedule: (): [
      ModuleType,
      EntityType,
      ActionType,
      "depreciation_schedule"
    ] => ["ax", "facilities", "choices", "depreciation_schedule"],

    types: (
      remoteUtilityId: string
    ): [ModuleType, EntityType, ActionType, "types", string] => [
      "ax",
      "facilities",
      "choices",
      "types",
      remoteUtilityId,
    ],

    utilityServices: (
      remoteUtilityId: string
    ): [ModuleType, EntityType, ActionType, "utility_services", string] => [
      "ax",
      "facilities",
      "choices",
      "utility_services",
      remoteUtilityId,
    ],

    areas: (
      remoteUtilityId: string
    ): [ModuleType, EntityType, ActionType, "areas", string] => [
      "ax",
      "facilities",
      "choices",
      "areas",
      remoteUtilityId,
    ],

    zones: (
      remoteUtilityId: string
    ): [ModuleType, EntityType, ActionType, "zones", string] => [
      "ax",
      "facilities",
      "choices",
      "zones",
      remoteUtilityId,
    ],

    managers: (
      remoteUtilityId: string
    ): [ModuleType, EntityType, ActionType, "managers", string] => [
      "ax",
      "facilities",
      "choices",
      "managers",
      remoteUtilityId,
    ],
  },
  serviceOrders: {
    list: (remoteUtilityId: string, filters?: ServiceOrderFilters) => [
      "ax",
      "facilities",
      "serviceOrders",
      "list",
      remoteUtilityId,
      filters,
    ],
  },
  finance: {
    finance: (facilityId: number, remoteUtilityId: string) => [
      "ax",
      "facilities",
      "finance",
      facilityId,
      remoteUtilityId,
    ],
  },
  system: {
    system: (facilityId: number) => ["ax", "facilities", "finance", facilityId],
  },

  networks: {
    list: (remoteUtilityId: string, filters: NetworkFilters) => [
      "ax",
      "facilities",
      "networks",
      "list",
      remoteUtilityId,
      filters,
    ],
  },
  assets: {
    list: (
      remoteUtilityId: string,
      search: string,
      systemNetwork: string,
      location: string,
      conditionScore: string,
      facilityId: number
    ) => [
      "ax",
      "assets",
      "list",
      remoteUtilityId,
      search || "",
      systemNetwork || "",
      location || "",
      conditionScore || "",
      facilityId.toString(),
    ],
  },
};
